import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Trans } from 'react-i18next';
import * as api from '../../api';
import * as logger from '../../logger';
import { Notifications } from '../../scenes/Notifications';
import { createLog, getFormStartPath } from '../../utils/form';
import { isAppointmentCancellableNow } from '../../utils/confirmCancel';
import { useIsAuthenticated } from '../authentication/useIsAuthenticated';

interface UseAppointmentsReturnValues {
  isLoading: boolean;
  onReplySubmit: (
    appointments: api.NotificationIntent.Appointment[],
    reply: api.EhrTimeSlot.AppointmentStatus,
  ) => Promise<void>;
  isSubmitting: boolean;
  appointments: api.NotificationIntent.Appointment[] | null;
  appointmentsToConfirm?: api.NotificationIntent.Appointment[];
  appointmentsConfirmed?: api.NotificationIntent.Appointment[];
  appointmentsCanceled?: api.NotificationIntent.Appointment[];
  appointmentsRescheduled?: api.NotificationIntent.Appointment[];
  getAppointments: () => Promise<void>;
  onConfirmed?: () => void;
  anyReschedulable?: boolean;
  skipConfirmationScreen?: boolean;
}

/** Fetch and manage appointments for given authentication code */
export const useAppointments = (
  authenticationCode: string,
): UseAppointmentsReturnValues => {
  const history = useHistory();
  const [appointments, setAppointments] = useState<
    api.NotificationIntent.Appointment[] | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSetRedirectEnabled, setFormSetRedirectEnabled] = useState(false);
  const [multipleAppointmentsEnabled, setMultipleAppointmentsEnabled] =
    useState(false);
  const [skipConfirmationScreen, setSkipConfirmationScreen] = useState(false);
  const { isAuthenticated } = useIsAuthenticated(authenticationCode);

  const appointmentsToConfirm = appointments?.filter(
    (appointment) => !appointment.encounter.status,
  );

  const appointmentsConfirmed = appointments?.filter(
    (appointment) =>
      appointment.encounter.status ===
      api.EhrTimeSlot.AppointmentStatus.Confirmed,
  );
  const appointmentsCanceled = appointments?.filter(
    (appointment) =>
      appointment.encounter.status ===
        api.EhrTimeSlot.AppointmentStatus.Cancelled ||
      appointment.encounter.status ===
        api.EhrTimeSlot.AppointmentStatus.CancellationIntended,
  );
  const appointmentsRescheduled = appointments?.filter(
    (appointment) =>
      appointment.encounter.status ===
      api.EhrTimeSlot.AppointmentStatus.Rescheduled,
  );

  const anyReschedulable = !!appointments?.find(
    (appointment) =>
      appointment.rescheduleAppointmentKind &&
      isAppointmentCancellableNow(appointment),
  );
  const getAppointments = useCallback(async () => {
    try {
      setIsLoading(true);
      const {
        appointments,
        formSetRedirectEnabled,
        multipleAppointmentsEnabled,
        skipConfirmationScreen,
      } =
        await api.client.NotificationIntent.getAppointments(authenticationCode);
      setAppointments(appointments);
      setFormSetRedirectEnabled(!!formSetRedirectEnabled);
      setMultipleAppointmentsEnabled(!!multipleAppointmentsEnabled);
      setSkipConfirmationScreen(!!skipConfirmationScreen);
    } catch (e) {
      // Don't log to sentry here. Primary is already doing it.
    } finally {
      setIsLoading(false);
    }
  }, [authenticationCode]);

  useEffect(() => {
    if (!isSubmitting) {
      getAppointments().catch((e) => undefined);
    }
  }, [getAppointments, isSubmitting]);

  const onReplySubmit = async (
    appointments: api.NotificationIntent.Appointment[],
    replyStatus: api.EhrTimeSlot.AppointmentStatus,
  ) => {
    let numberOfFailedCancellations = 0;
    try {
      const replies = appointments.map((appointment) => ({
        appointmentStatus: replyStatus,
        encounterId: appointment.encounter.id,
      }));
      setIsSubmitting(true);
      const result =
        await api.client.NotificationIntent.processAppointmentsConfirmation({
          authenticationCode,
          appointmentReplies: replies,
        });
      numberOfFailedCancellations =
        replies.length - result.successfullyProcessedIntentEncounterIds.length;
    } catch (e) {
      logger.error(e, `useAppointments.onReplySubmit failed`);
    } finally {
      setIsSubmitting(false);
    }
    if (replyStatus === api.EhrTimeSlot.AppointmentStatus.Cancelled) {
      if (numberOfFailedCancellations > 0) {
        Notifications.notify({
          body: (
            <Trans
              i18nKey="cancelAppointmentsFailedNotif"
              count={numberOfFailedCancellations}
              values={{ numberOfFailedCancellations }}
            />
          ),
          vertical: 'top',
        });
      } else {
        const numberOfSuccessfulCancellations = appointments.length;
        Notifications.notify({
          body: (
            <Trans
              i18nKey="cancelAppointmentsSucceededNotif"
              count={numberOfSuccessfulCancellations}
              values={{ numberOfSuccessfulCancellations }}
            />
          ),
          vertical: 'top',
        });
      }
    }
  };

  const onConfirmed = formSetRedirectEnabled
    ? async () => {
        if (isAuthenticated) {
          history.push(
            getFormStartPath({
              authenticationCode,
              multi: multipleAppointmentsEnabled,
              allowReview: true,
            }),
          );
          return;
        }
        await createLog(
          'appointment_confirmed',
          authenticationCode,
          'Appointment confirmed',
        );
        history.push({
          pathname: `/login/${authenticationCode}`,
          search: multipleAppointmentsEnabled ? `?future=true` : '',
        });
      }
    : undefined;

  return {
    appointmentsToConfirm,
    appointmentsConfirmed,
    appointmentsCanceled,
    appointmentsRescheduled,
    isLoading,
    onReplySubmit,
    isSubmitting,
    appointments,
    onConfirmed,
    getAppointments,
    anyReschedulable,
    skipConfirmationScreen,
  };
};
